import config from '../config'

// 다운로드 패키징 전역 감시자 (원천데이터 · 라벨데이터 · 학습데이터셋 공용).
//
// 패키징/재조립 폴링을 각 화면 컴포넌트 안에 두면 다른 화면으로 이동하는 순간
// 폴링이 죽어서, 완료돼도 알림 없이 버튼만 조용히 돌아와 있었다. 이 모듈은
// 컴포넌트 수명과 무관하게 탭이 살아있는 동안 localStorage의 진행 건을 폴링해,
// 어느 화면에 있든 완료 알림 + 자동 다운로드를 보장한다. (새로고침해도
// localStorage 기록으로 감시가 재개된다)
//
// 키는 영역 접두사를 붙인 문자열이다: 'raw:<batchId>' · 'label:<versionId>' ·
// 'dataset:<nodeId>'. 항목마다 폴링 URL(statusUrl)을 따로 가지므로 영역별로
// 다른 상태 엔드포인트를 그대로 쓴다. 응답 규약은 세 영역이 동일하다:
//   { status: 'ready' | 'failed' | <processing>, progress?, packed_bytes?, message? }
//
// 컴포넌트와의 통신은 window 이벤트로 한다:
//   packaging:progress { key, kind, progress?, packedBytes? }
//   packaging:done     { key, kind, ok }

export type PackagingEntry = {
  /** 폴링할 절대 URL (Bearer 헤더로 호출) */
  statusUrl: string
  /** 완료 시 네이티브 다운로드 URL (access_token 쿼리 포함) */
  downloadUrl: string
  ts: number
  /** 데이터 영역 — 알림이 어느 화면에서든 뜨므로 출처를 스스로 밝힌다 (예: '원천데이터') */
  kind?: string
  /** 무엇이 완료됐는지: 노드 경로 · 파일명 등 */
  name?: string
  /** V1(원천데이터 전용 시절) 항목 호환용 */
  exportPath?: string
}

const STORE_KEY = 'packagingWatcherV2'
const LEGACY_STORE_KEY = 'rawBatchPackagingV1'
const POLL_MS = 3000
const MAX_AGE_MS = 24 * 3600 * 1000

const migrateLegacy = () => {
  try {
    const raw = localStorage.getItem(LEGACY_STORE_KEY)
    if (!raw) return
    const legacy = JSON.parse(raw) as Record<string, any>
    const cur = JSON.parse(localStorage.getItem(STORE_KEY) || '{}')
    Object.entries(legacy).forEach(([bid, ent]) => {
      if (!ent || !ent.exportPath || !ent.downloadUrl) return
      cur[`raw:${bid}`] = {
        statusUrl: `${config.API_BASE_URL}/files/downloads/status?export_path=${encodeURIComponent(ent.exportPath)}`,
        downloadUrl: ent.downloadUrl,
        ts: ent.ts || Date.now(),
        kind: ent.kind || '원천데이터',
        name: ent.name,
      }
    })
    localStorage.setItem(STORE_KEY, JSON.stringify(cur))
    localStorage.removeItem(LEGACY_STORE_KEY)
  } catch {}
}

const readStore = (): Record<string, PackagingEntry> => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) || '{}') } catch { return {} }
}
const writeStore = (m: Record<string, PackagingEntry>) => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(m)) } catch {}
}
const removeEntry = (key: string) => {
  const m = readStore()
  delete m[key]
  writeStore(m)
}

const emit = (type: string, detail: unknown) => {
  try { window.dispatchEvent(new CustomEvent(type, { detail })) } catch {}
}

let timer: ReturnType<typeof setInterval> | null = null
let ticking = false

const stopIfIdle = () => {
  if (timer && Object.keys(readStore()).length === 0) {
    clearInterval(timer)
    timer = null
  }
}

const finish = (key: string, entry: PackagingEntry, ok: boolean, message?: string) => {
  removeEntry(key)
  if (ok && entry.downloadUrl) {
    const a = document.createElement('a')
    a.href = entry.downloadUrl
    a.rel = 'noopener'
    a.click()
  }
  emit('packaging:done', { key, kind: entry.kind, ok })
  // 다른 화면에 있어도 완료/실패를 알린다 (앱 전반의 기존 알림 방식과 동일하게 alert 사용)
  const prefix = entry.kind ? `[${entry.kind}] ` : ''
  if (ok) {
    alert(`${prefix}패키징이 완료되어 다운로드를 시작합니다.${entry.name ? `\n\n${entry.name}` : ''}`)
  } else {
    alert(`${prefix}${message || '패키징에 실패했습니다. 다시 시도해주세요.'}${entry.name ? `\n\n${entry.name}` : ''}`)
  }
  stopIfIdle()
}

const tick = async () => {
  if (ticking) return
  ticking = true
  try {
    const store = readStore()
    const keys = Object.keys(store)
    if (keys.length === 0) { stopIfIdle(); return }
    const token = localStorage.getItem('access_token')
    for (const key of keys) {
      const ent = store[key]
      if (!ent || !ent.statusUrl || Date.now() - (ent.ts || 0) > MAX_AGE_MS) {
        removeEntry(key)
        continue
      }
      try {
        const res = await fetch(ent.statusUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        })
        if (!res.ok) continue
        const data = await res.json()
        if (data.status === 'ready') {
          finish(key, ent, true)
        } else if (data.status === 'failed') {
          finish(key, ent, false, data.message)
        } else {
          emit('packaging:progress', {
            key,
            kind: ent.kind,
            progress: data.progress,
            packedBytes: data.packed_bytes,
          })
        }
      } catch {}
    }
  } finally {
    ticking = false
  }
}

const ensureTimer = () => {
  if (!timer) {
    timer = setInterval(tick, POLL_MS)
  }
}

/** 패키징 시작 시 호출 — 저장 + 감시 시작. key 예: 'raw:193', 'label:55', 'dataset:120' */
export const trackPackaging = (key: string, entry: PackagingEntry) => {
  const m = readStore()
  m[key] = entry
  writeStore(m)
  ensureTimer()
}

/** 감시 중인 키 목록 (마운트 시 버튼 상태 복원용). prefix 예: 'raw:' */
export const trackedKeys = (prefix?: string): string[] => {
  const keys = Object.keys(readStore())
  return prefix ? keys.filter(k => k.startsWith(prefix)) : keys
}

/** 저장된 진행 건이 있으면 감시 재개 (페이지 로드 시 1회) */
export const resumeWatcher = () => {
  migrateLegacy()
  if (Object.keys(readStore()).length > 0) {
    ensureTimer()
    void tick()
  }
}

// 모듈 로드 시 자동 재개 — App 진입점에서 import 하면 새로고침 후에도 이어진다
resumeWatcher()
